(() => {
  if (window.AnonaTopBarsLoaded) return;
  window.AnonaTopBarsLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMedia = window.matchMedia('(max-width: 699px)');
  const rotationVectors = {
    up: ['0, -100%', '0, 100%'],
    down: ['0, 100%', '0, -100%'],
    left: ['-100%, 0', '100%, 0'],
    right: ['100%, 0', '-100%, 0']
  };

  function getDirection(element) {
    const direction = element.getAttribute('rotation-direction');
    return direction in rotationVectors ? direction : 'up';
  }

  function getOppositeDirection(direction) {
    return { up: 'down', down: 'up', left: 'right', right: 'left' }[direction];
  }

  function isCarouselEnabled(element) {
    return element.hasAttribute(mobileMedia.matches ? 'mobile-carousel' : 'desktop-carousel');
  }

  function getAnimationFrames(element, direction) {
    const [exit, enter] = rotationVectors[direction] || rotationVectors.up;
    const effect = element.dataset.transitionEffect || 'slide';
    const slide = effect !== 'fade';
    const fade = effect !== 'slide';
    return [
      [{ transform: 'translate3d(0, 0, 0)', opacity: 1 }, { transform: slide ? `translate3d(${exit}, 0)` : 'translate3d(0, 0, 0)', opacity: fade ? 0 : 1 }],
      [{ transform: slide ? `translate3d(${enter}, 0)` : 'translate3d(0, 0, 0)', opacity: fade ? 0 : 1 }, { transform: 'translate3d(0, 0, 0)', opacity: 1 }]
    ];
  }

  class CampaignTopBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.slides = Array.from(this.querySelectorAll('[data-campaign-slide]'));
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.transitioning = false;
      this.paused = false;
      this.touchStart = null;
      this.abortController = new AbortController();

      if (this.slides.length < 2) return;

      const signal = this.abortController.signal;
      mobileMedia.addEventListener('change', () => this.updateMode(), { signal });
      this.addEventListener('mouseenter', () => this.pause(), { signal });
      this.addEventListener('mouseleave', () => this.resume(), { signal });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener('focusout', () => this.resume(), { signal });
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') this.touchStart = { x: event.clientX, y: event.clientY };
      }, { signal });
      this.addEventListener('pointerup', (event) => {
        if (!this.touchStart) return;
        if (!isCarouselEnabled(this)) {
          this.touchStart = null;
          return;
        }
        const direction = getDirection(this);
        const vertical = direction === 'up' || direction === 'down';
        const distance = vertical ? event.clientY - this.touchStart.y : event.clientX - this.touchStart.x;
        this.touchStart = null;
        if (Math.abs(distance) < 40) return;
        this.select(this.index + (distance < 0 ? 1 : -1), vertical ? (distance < 0 ? 'up' : 'down') : (distance < 0 ? 'left' : 'right'));
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-campaign-slide]');
        const index = this.slides.indexOf(slide);
        if (index >= 0) {
          this.pause(true);
          this.select(index, index >= this.index ? getDirection(this) : getOppositeDirection(getDirection(this)), false);
        }
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => this.resume(true), { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.pause() : this.resume(), { signal });

      this.updateMode();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.autoplayTimer);
    }

    pause(editorPause = false) {
      this.paused = true;
      if (editorPause) this.editorPaused = true;
      window.clearTimeout(this.autoplayTimer);
    }

    resume(editorResume = false) {
      if (editorResume) this.editorPaused = false;
      if (this.editorPaused || this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;
      this.paused = false;
      this.startAutoplay();
    }

    updateMode() {
      window.clearTimeout(this.autoplayTimer);
      this.slides.forEach((slide, index) => {
        slide.getAnimations().forEach((animation) => animation.cancel());
        slide.classList.toggle('is-active', index === this.index);
        slide.toggleAttribute('aria-hidden', index !== this.index);
      });
      this.startAutoplay();
    }

    startAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      if (!isCarouselEnabled(this) || this.paused || reduceMotion.matches || this.slides.length < 2) return;
      const delay = Math.max(1, Number(this.getAttribute('autoplay')) || 3) * 1000;
      this.autoplayTimer = window.setTimeout(() => this.select(this.index + 1), delay);
    }

    async select(requestedIndex, direction = getDirection(this), animate = true) {
      if (this.transitioning || this.slides.length < 2 || (!isCarouselEnabled(this) && animate)) return;
      const nextIndex = (requestedIndex + this.slides.length) % this.slides.length;
      if (nextIndex === this.index) return;

      window.clearTimeout(this.autoplayTimer);
      const fromSlide = this.slides[this.index];
      const toSlide = this.slides[nextIndex];
      const duration = Math.max(0, Number(this.dataset.transitionDuration) || 300);
      const shouldAnimate = animate && !reduceMotion.matches && duration > 0;

      this.transitioning = true;
      toSlide.classList.add('is-active');
      toSlide.removeAttribute('aria-hidden');
      fromSlide.setAttribute('aria-hidden', 'true');

      if (shouldAnimate) {
        const [fromFrames, toFrames] = getAnimationFrames(this, direction);
        const options = { duration, easing: 'ease', fill: 'both' };
        const animations = [fromSlide.animate(fromFrames, options), toSlide.animate(toFrames, options)];
        await Promise.all(animations.map((animation) => animation.finished.catch(() => undefined)));
        animations.forEach((animation) => animation.cancel());
      }

      fromSlide.classList.remove('is-active');
      this.index = nextIndex;
      this.transitioning = false;
      this.slides.forEach((slide, index) => slide.toggleAttribute('aria-hidden', index !== this.index));
      this.startAutoplay();
    }
  }

  class DailyTopBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.items = Array.from(this.querySelectorAll('[data-daily-item]'));
      this.index = 0;
      this.paused = false;
      this.transitioning = false;
      this.touchStart = null;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      mobileMedia.addEventListener('change', () => this.updateMode(), { signal });
      this.addEventListener('mouseenter', () => this.pause(), { signal });
      this.addEventListener('mouseleave', () => this.resume(), { signal });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener('focusout', () => this.resume(), { signal });
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') this.touchStart = { x: event.clientX, y: event.clientY };
      }, { signal });
      this.addEventListener('pointerup', (event) => {
        if (!this.touchStart) return;
        if (!isCarouselEnabled(this)) {
          this.touchStart = null;
          return;
        }
        const direction = getDirection(this);
        const vertical = direction === 'up' || direction === 'down';
        const distance = vertical ? event.clientY - this.touchStart.y : event.clientX - this.touchStart.x;
        this.touchStart = null;
        if (Math.abs(distance) < 40) return;
        this.select(this.index + (distance < 0 ? 1 : -1), vertical ? (distance < 0 ? 'up' : 'down') : (distance < 0 ? 'left' : 'right'));
      }, { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.pause() : this.resume(), { signal });

      this.updateMode();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.autoplayTimer);
    }

    updateMode() {
      window.clearTimeout(this.autoplayTimer);
      const carouselLayout = mobileMedia.matches || isCarouselEnabled(this);
      this.dataset.desktopCarouselActive = String(!mobileMedia.matches && isCarouselEnabled(this));
      this.items.forEach((item, index) => {
        item.getAnimations().forEach((animation) => animation.cancel());
        item.classList.toggle('is-active', carouselLayout && index === this.index);
        if (!carouselLayout || index === this.index) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');
      });
      this.startAutoplay();
    }

    async select(requestedIndex, direction = getDirection(this)) {
      if (!isCarouselEnabled(this) || this.items.length < 2 || this.transitioning) return;
      const nextIndex = (requestedIndex + this.items.length) % this.items.length;
      if (nextIndex === this.index) return;

      window.clearTimeout(this.autoplayTimer);
      const fromItem = this.items[this.index];
      const toItem = this.items[nextIndex];
      const duration = Math.max(0, Number(this.dataset.transitionDuration) || 300);

      this.transitioning = true;
      toItem.classList.add('is-active');
      if (!reduceMotion.matches && duration > 0) {
        const [fromFrames, toFrames] = getAnimationFrames(this, direction);
        const options = { duration, easing: 'ease', fill: 'both' };
        const animations = [fromItem.animate(fromFrames, options), toItem.animate(toFrames, options)];
        await Promise.all(animations.map((animation) => animation.finished.catch(() => undefined)));
        animations.forEach((animation) => animation.cancel());
      }

      fromItem.classList.remove('is-active');
      this.index = nextIndex;
      this.transitioning = false;
      this.items.forEach((item, index) => item.toggleAttribute('aria-hidden', index !== this.index));
      this.startAutoplay();
    }

    pause() {
      this.paused = true;
      window.clearTimeout(this.autoplayTimer);
    }

    resume() {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;
      this.paused = false;
      this.startAutoplay();
    }

    startAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      if (!isCarouselEnabled(this) || this.paused || reduceMotion.matches || this.items.length < 2) return;
      const delay = Math.max(1, Number(this.getAttribute('autoplay')) || 3) * 1000;
      this.autoplayTimer = window.setTimeout(() => this.select(this.index + 1), delay);
    }
  }

  if (!customElements.get('campaign-top-bar')) customElements.define('campaign-top-bar', CampaignTopBar);
  if (!customElements.get('daily-top-bar')) customElements.define('daily-top-bar', DailyTopBar);

  const observedStackSections = new WeakSet();
  const stackResizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateHeaderStack) : null;
  let stackFrame;

  function updateHeaderStack() {
    window.cancelAnimationFrame(stackFrame);
    stackFrame = window.requestAnimationFrame(() => {
      const sections = Array.from(document.querySelectorAll(
        '.shopify-section--announcement-bar, .shopify-section--daily-top-bar, .shopify-section--campaign-top-bar, .shopify-section--header'
      ));
      let stickyOffset = 0;

      sections.forEach((section) => {
        section.style.setProperty('--header-stack-top', `${stickyOffset}px`);
        if (!observedStackSections.has(section)) {
          stackResizeObserver?.observe(section);
          observedStackSections.add(section);
        }

        const style = getComputedStyle(section);
        const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && section.getClientRects().length > 0;
        const isSticky = style.position === 'sticky' || style.position === 'fixed';
        if (isVisible && isSticky) stickyOffset += section.getBoundingClientRect().height;
      });

      // A sticky element can still sit in its natural document position before it
      // reaches its sticky threshold. Use its live bottom edge so the fixed
      // promotion bar never overlaps that transitional header stack.
      const liveStackBottom = sections.reduce((bottom, section) => {
        const style = getComputedStyle(section);
        const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && section.getClientRects().length > 0;
        const isSticky = style.position === 'sticky' || style.position === 'fixed';
        if (!isVisible || !isSticky) return bottom;

        return Math.max(bottom, section.getBoundingClientRect().bottom);
      }, 0);

      document.documentElement.style.setProperty(
        '--sticky-promotion-dynamic-top',
        `${Math.max(0, liveStackBottom)}px`
      );
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateHeaderStack, { once: true });
  else updateHeaderStack();

  window.addEventListener('resize', updateHeaderStack, { passive: true });
  window.addEventListener('scroll', updateHeaderStack, { passive: true });
  document.addEventListener('shopify:section:load', updateHeaderStack);
  document.addEventListener('shopify:section:unload', updateHeaderStack);
  document.addEventListener('shopify:section:reorder', updateHeaderStack);
})();
